import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Divider,
  Switch,
  CircularProgress,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import UserApi from "../api/UserApi";
import AddUserDialog from "../components/AddUserDialog"; // Import the AddUserDialog
import CustomDialog from "../components/CustomDialog";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true); // Start loading
    try {
      const response = await UserApi.fetchAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedUser) {
      try {
        await UserApi.deleteUser(selectedUser.id);
        setUsers(users.filter((user) => user.id !== selectedUser.id));
        setDeleteDialogOpen(false); // Close dialog after deletion
        setSelectedUser(null);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  const handleToggleRole = async (userId) => {
    try {
      const response = await UserApi.toggleUserRole(userId);
      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, role: response.data.newRole } : user
        )
      );
    } catch (error) {
      console.error("Error toggling role:", error);
    }
  };

  const handleAddUser = (newUser) => {
    setUsers((prevUsers) => [...prevUsers, newUser]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Manage Users
      </Typography>
      <Divider sx={{ marginBottom: 2 }} />
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px", // Adjust the height as needed
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Switch
                        checked={user.role === 1}
                        onChange={() => handleToggleRole(user.id)}
                        color="primary"
                      />
                      {user.role === 1 ? "Admin" : "User"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteDialogOpen(true); // Open confirmation dialog
                        }}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => setAddUserDialogOpen(true)} // Open the AddUserDialog
          >
            Add User
          </Button>
          <AddUserDialog
            open={addUserDialogOpen}
            onClose={() => setAddUserDialogOpen(false)}
            onUserAdded={handleAddUser} // Callback to update users
          />
        </>
      )}

      {/* Custom Warning Dialog */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        type="warning"
        title="Confirm Deletion"
        message={`Are you sure you want to delete user "${selectedUser?.name}"?`}
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteConfirm}
        secondaryButtonText="Cancel"
        secondaryButtonAction={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default ManageUsers;
